import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Photo slider — fills its half of the row with an auto-advancing gallery.
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const count = photos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || i}
          url={photo.image!}
          alt={photo.alt_text || photo.title || heading}
          className={`amenities-slider-img${i === current ? ' amenities-slider-img--active' : ''}`}
          sizes="(max-width: 768px) 100vw, 50vw"
          priority
        />
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>

          <div className="amenities-slider-dots">
            {photos.map((_, i) => (
              <button
                key={i}
                className={`amenities-slider-dot${i === current ? ' amenities-slider-dot--active' : ''}`}
                onClick={() => setCurrent(i)}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Category row — a full-bleed 50/50 split: large photo on one side, a centered
// overline + heading + plain-text amenity list on the other. Rows stagger the
// image left/right.
// ============================================================================

// Max amenities shown per page before pagination controls appear.
const ITEMS_PER_PAGE = 8;

interface AmenityRowProps {
  overlineNode: ReactNode;
  headingNode: ReactNode;
  headingText: string;
  items: Amenity[];
  photos: GalleryPhoto[];
  imagePosition: 'left' | 'right';
}

function AmenityRow({ overlineNode, headingNode, headingText, items, photos, imagePosition }: AmenityRowProps) {
  const rowRef = useScrollReveal<HTMLDivElement>();
  const hasImage = photos.length > 0;

  const [page, setPage] = useState(0);

  const totalPages = Math.max(1, Math.ceil(items.length / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const currentItems = items.slice(
    safePage * ITEMS_PER_PAGE,
    (safePage + 1) * ITEMS_PER_PAGE
  );

  const contentPanel = (
    <div className="amenities-row-content">
      <div className="amenities-row-head">
        {overlineNode}
        {headingNode}
      </div>
      <ul className="amenities-col-list">
        {currentItems.map((item, i) => (
          <li key={`p${safePage}-${i}`} className="amenities-col-item">
            {item.title || ''}
          </li>
        ))}
      </ul>

      {totalPages > 1 && (
        <div className="amenities-pagination">
          <button
            type="button"
            className="amenities-pagination-btn"
            onClick={() => setPage(Math.max(0, safePage - 1))}
            disabled={safePage === 0}
            aria-label="Previous amenities page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <span className="amenities-pagination-info">
            {safePage + 1} / {totalPages}
          </span>
          <button
            type="button"
            className="amenities-pagination-btn"
            onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
            disabled={safePage === totalPages - 1}
            aria-label="Next amenities page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
      )}
    </div>
  );

  const imagePanel = hasImage ? (
    <div className="amenities-row-image">
      <PhotoSlider photos={photos} heading={headingText} />
    </div>
  ) : null;

  return (
    <div
      ref={rowRef}
      className={`reveal-fade-up amenities-row amenities-row--img-${imagePosition}${hasImage ? '' : ' amenities-row--no-image'}`}
    >
      {imagePosition === 'left' ? (
        <>
          {imagePanel}
          {contentPanel}
        </>
      ) : (
        <>
          {contentPanel}
          {imagePanel}
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function AmenitiesList() {
  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  useRevealStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;

  return (
    <section className="bg-[var(--vibe-color-background)]">
      {hasContent ? (
        <div className="amenities-stack">
          {communityItems.length > 0 && (
            <AmenityRow
              overlineNode={
                <RichTextContent
                  path="amenitieslist.communityOverline"
                  label="Community Overline"
                  defaultValue="<p>Community</p>"
                  as="p"
                  className="amenities-row-overline"
                />
              }
              headingNode={
                <RichTextContent
                  path="amenitieslist.communityHeading"
                  label="Community Heading"
                  defaultValue="<p>Amenities</p>"
                  as="h2"
                  className="amenities-row-heading"
                />
              }
              headingText="Community Amenities"
              items={communityItems}
              photos={communityPhotos}
              imagePosition="left"
            />
          )}
          {apartmentItems.length > 0 && (
            <AmenityRow
              overlineNode={
                <RichTextContent
                  path="amenitieslist.apartmentOverline"
                  label="Interior Overline"
                  defaultValue="<p>Interior</p>"
                  as="p"
                  className="amenities-row-overline"
                />
              }
              headingNode={
                <RichTextContent
                  path="amenitieslist.apartmentHeading"
                  label="Interior Heading"
                  defaultValue="<p>Features</p>"
                  as="h2"
                  className="amenities-row-heading"
                />
              }
              headingText="Interior Features"
              items={apartmentItems}
              photos={apartmentPhotos}
              imagePosition="right"
            />
          )}
        </div>
      ) : (
        <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Amenities will be displayed here.</p>
        </div>
      )}

      <style>{`
        .amenities-stack {
          display: flex;
          flex-direction: column;
        }

        /* Full-bleed 50/50 split row. Image cell and content cell stretch to
           the same (tallest) height automatically via the grid. */
        .amenities-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          align-items: stretch;
          min-height: 600px;
          background: var(--vibe-color-background);
        }
        .amenities-row--no-image {
          grid-template-columns: 1fr;
          min-height: 0;
        }

        /* Content panel — vertically + horizontally centered */
        .amenities-row-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 4rem 3rem;
        }

        /* Image panel fills its cell */
        .amenities-row-image {
          position: relative;
          overflow: hidden;
        }

        .amenities-row-head {
          margin-bottom: 1.75rem;
        }

        .amenities-row-overline {
          font-family: var(--vibe-font-body);
          text-transform: uppercase;
          letter-spacing: 0.28em;
          font-size: 0.9rem;
          font-weight: 500;
          color: var(--vibe-color-accent);
          margin: 0 0 0.5rem;
        }

        .amenities-row-heading {
          font-family: var(--vibe-font-heading);
          font-size: 3rem;
          font-weight: 700;
          line-height: 1.05;
          color: var(--vibe-color-text);
          margin: 0;
        }

        /* Plain-text, centered, single-column amenity list */
        .amenities-col-list {
          list-style: none;
          margin: 0;
          padding: 0;
          max-width: 34rem;
        }

        .amenities-col-item {
          font-family: var(--vibe-font-body);
          font-size: 0.98rem;
          color: var(--vibe-color-text);
          line-height: 1.3;
          padding: 0.3rem 0;
        }

        /* Pagination controls — only rendered when a list overflows one page */
        .amenities-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          margin-top: 1.75rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          color: var(--vibe-color-text);
        }
        .amenities-pagination-btn {
          width: 34px;
          height: 34px;
          padding: 0;
          border-radius: 50%;
          border: 1px solid var(--vibe-color-border);
          background: var(--vibe-color-background);
          color: var(--vibe-color-text);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, color 0.2s, opacity 0.2s;
        }
        .amenities-pagination-btn:hover:not(:disabled) {
          background: var(--vibe-color-accent);
          border-color: var(--vibe-color-accent);
          color: var(--vibe-color-text-on-secondary);
        }
        .amenities-pagination-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
        .amenities-pagination-info {
          min-width: 3rem;
          text-align: center;
          font-variant-numeric: tabular-nums;
        }

        /* Photo slider */
        .amenities-slider {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }
        .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slider-img--active {
          opacity: 1;
        }

        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.35);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s, background 0.3s;
          z-index: 2;
        }
        .amenities-slider:hover .amenities-slider-arrow {
          opacity: 1;
        }
        .amenities-slider-arrow:hover {
          background: rgba(0, 0, 0, 0.55);
        }
        .amenities-slider-arrow--prev { left: 14px; }
        .amenities-slider-arrow--next { right: 14px; }

        .amenities-slider-dots {
          position: absolute;
          bottom: 14px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 7px;
          z-index: 2;
        }
        .amenities-slider-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          border: 1.5px solid rgba(255, 255, 255, 0.7);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.25s, border-color 0.25s, transform 0.25s;
        }
        .amenities-slider-dot:hover {
          transform: scale(1.25);
          border-color: white;
        }
        .amenities-slider-dot--active {
          background: white;
          border-color: white;
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slider-img {
            transition: none;
          }
        }

        /* Tablet */
        @media (max-width: 992px) {
          .amenities-row {
            min-height: 520px;
          }
          .amenities-row-content {
            padding: 3rem 2rem;
          }
          .amenities-row-heading {
            font-size: 2.4rem;
          }
          .amenities-col-item {
            font-size: 0.92rem;
          }
        }

        /* Mobile: stack image on top, content below (regardless of stagger) */
        @media (max-width: 768px) {
          .amenities-row {
            grid-template-columns: 1fr;
            grid-template-rows: 300px auto;
            min-height: 0;
          }
          .amenities-row--no-image {
            grid-template-rows: auto;
          }
          .amenities-row--img-right > .amenities-row-content {
            order: 2;
          }
          .amenities-row--img-right > .amenities-row-image {
            order: 1;
          }
          .amenities-row-image {
            height: 300px;
          }
          .amenities-row-content {
            padding: 2.5rem 1.5rem;
          }
          .amenities-row-heading {
            font-size: 2rem;
          }
          .amenities-col-item {
            font-size: 0.88rem;
            padding: 0.25rem 0;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityOverline': {
      label: 'Community Overline',
      type: 'richtext',
      defaultValue: '<p>Community</p>',
    },
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Amenities</p>',
    },
    'amenitieslist.apartmentOverline': {
      label: 'Interior Overline',
      type: 'richtext',
      defaultValue: '<p>Interior</p>',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Interior Heading',
      type: 'richtext',
      defaultValue: '<p>Features</p>',
    },
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as any).contentSchema = contentSchema;

export default AmenitiesList;
